import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  SETTER_PERMISSION_KEYS,
  CLOSER_PERMISSION_KEYS,
  teamPermissionKeySchema,
  PERMISSION_META,
  type TeamPermissionKey,
} from '@/lib/team-permissions';
import { supabase } from '@/integrations/supabase/client';

export interface TeamPermissionsEditorProps {
  userId: string;
  role: 'setter' | 'closer';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Phase 16.2-06 refactor — dual export.
 *
 * `TeamPermissionsSection` is the section-only component (no Dialog chrome) that
 * can be embedded inside another modal (e.g. EditTeamMemberModal).
 *
 * `TeamPermissionsEditor` is the standalone-modal wrapper preserved for
 * backward-compat with RolesPermissions.tsx's "Edit Permissions" dropdown action.
 */
export interface TeamPermissionsSectionProps {
  userId: string;
  role: 'setter' | 'closer';
  /**
   * 'standalone' (default): Switch toggles upsert directly to
   *   team_member_permissions via supabase (behavior preserved from Phase 16).
   * 'pending': Switch toggles call onPendingChange; no DB write — parent collects
   *   the change-set and flushes through update-team-member on save.
   */
  mode?: 'standalone' | 'pending';
  pendingChanges?: Record<string, boolean>;
  onPendingChange?: (key: string, enabled: boolean) => void;
}

export function TeamPermissionsSection({
  userId,
  role,
  mode = 'standalone',
  pendingChanges,
  onPendingChange,
}: TeamPermissionsSectionProps) {
  const qc = useQueryClient();
  const keys = role === 'setter' ? SETTER_PERMISSION_KEYS : CLOSER_PERMISSION_KEYS;

  const { data: rows } = useQuery({
    queryKey: ['team_permissions_admin_edit', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_member_permissions')
        .select('permission_key, enabled, is_override')
        .eq('user_id', userId);
      if (error) throw error;
      return (data ?? []) as Array<{
        permission_key: string;
        enabled: boolean;
        is_override: boolean;
      }>;
    },
  });

  const map = new Map<string, { enabled: boolean; is_override: boolean }>();
  for (const r of rows ?? []) {
    map.set(r.permission_key, { enabled: r.enabled, is_override: r.is_override });
  }

  const toggle = useMutation({
    mutationFn: async ({ key, enabled }: { key: TeamPermissionKey; enabled: boolean }) => {
      // zod defense-in-depth (T-16-07): reject any key outside the enum BEFORE writing.
      const parsed = teamPermissionKeySchema.safeParse(key);
      if (!parsed.success) throw new Error('Invalid permission key');
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('team_member_permissions')
        .upsert(
          {
            user_id: userId,
            permission_key: key,
            enabled,
            is_override: true,
            updated_by: user?.id ?? null,
          },
          { onConflict: 'user_id,permission_key' },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Permission updated');
      qc.invalidateQueries({ queryKey: ['team_permissions_admin_edit', userId] });
      qc.invalidateQueries({ queryKey: ['team_permissions', userId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-3 py-2">
      {keys.map((key) => {
        const row = map.get(key);
        // In 'pending' mode, prefer the pending override value if set.
        const pendingVal = pendingChanges?.[key];
        const enabled =
          mode === 'pending' && pendingVal !== undefined
            ? pendingVal
            : row?.enabled ?? false;
        // Backward-compat: unknown keys (future additions missing PERMISSION_META entry)
        // fall back to raw-key title + empty description so the UI never crashes.
        const meta =
          (PERMISSION_META as Record<string, { title: string; description: string }>)[key] ??
          { title: key, description: '' };
        return (
          <div
            key={key}
            className="flex items-center justify-between gap-4 py-2 border-b border-border/50 last:border-0"
          >
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-sm font-medium">{meta.title}</span>
              {meta.description ? (
                <p className="text-xs text-muted-foreground">{meta.description}</p>
              ) : null}
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => {
                if (mode === 'pending') {
                  onPendingChange?.(key, v);
                } else {
                  toggle.mutate({ key, enabled: v });
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Backward-compat standalone-modal wrapper. Shape unchanged from Phase 16 so
 * the "Edit Permissions" dropdown-action launcher in RolesPermissions.tsx works
 * without modification.
 */
export function TeamPermissionsEditor({
  userId,
  role,
  open,
  onOpenChange,
}: TeamPermissionsEditorProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Permissions</DialogTitle>
          <DialogDescription>
            Toggle page-level access for this {role}. Changes save automatically.
          </DialogDescription>
        </DialogHeader>
        <TeamPermissionsSection userId={userId} role={role} mode="standalone" />
      </DialogContent>
    </Dialog>
  );
}
